import { useState, useEffect, useCallback } from 'react';

// Track loading state
let isScriptLoading = false;
let isScriptLoaded = false;
const loadCallbacks: Array<() => void> = [];

declare global {
  interface Window {
    pannellum: {
      viewer: (
        container: string | HTMLElement,
        config: PannellumConfig
      ) => PannellumViewer;
    };
  }
}

export interface PannellumConfig {
  type: 'equirectangular' | 'cubemap' | 'multires';
  panorama: string;
  autoLoad?: boolean;
  autoRotate?: number;
  compass?: boolean;
  showControls?: boolean;
  showFullscreenCtrl?: boolean;
  showZoomCtrl?: boolean;
  mouseZoom?: boolean;
  draggable?: boolean;
  hotSpots?: Array<{
    pitch: number;
    yaw: number;
    type: 'info' | 'scene';
    text?: string;
    sceneId?: string;
  }>;
  hfov?: number;
  minHfov?: number;
  maxHfov?: number;
  pitch?: number;
  yaw?: number;
  haov?: number;
  vaov?: number;
  vOffset?: number;
  title?: string;
  author?: string;
  strings?: {
    loadButtonLabel?: string;
    loadingLabel?: string;
    bylineLabel?: string;
    noPanoramaError?: string;
    fileAccessError?: string;
    malformedURLError?: string;
    iOS8WebGLError?: string;
    genericWebGLError?: string;
    textureSizeError?: string;
    unknownError?: string;
  };
}

export interface PannellumViewer {
  destroy: () => void;
  getYaw: () => number;
  getPitch: () => number;
  getHfov: () => number;
  setYaw: (yaw: number, animated?: boolean) => void;
  setPitch: (pitch: number, animated?: boolean) => void;
  setHfov: (hfov: number, animated?: boolean) => void;
  lookAt: (pitch: number, yaw: number, hfov?: number, animated?: boolean) => void;
  startAutoRotate: (speed?: number) => void;
  stopAutoRotate: () => void;
  isLoaded: () => boolean;
  toggleFullscreen: () => void;
  getRenderer: () => unknown;
  on: (event: string, callback: () => void) => void;
  off: (event: string, callback: () => void) => void;
}

function loadPannellumScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isScriptLoaded && window.pannellum) {
      resolve();
      return;
    }

    loadCallbacks.push(resolve);

    if (isScriptLoading) {
      return;
    }

    isScriptLoading = true;

    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js';
    script.async = true;

    script.onload = () => {
      isScriptLoaded = true;
      isScriptLoading = false;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };

    script.onerror = () => {
      isScriptLoading = false;
      reject(new Error('Failed to load Pannellum script'));
    };

    document.head.appendChild(script);
  });
}

export function usePannellum() {
  const [isLoaded, setIsLoaded] = useState(isScriptLoaded);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (isScriptLoaded) {
      setIsLoaded(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await loadPannellumScript();
      setIsLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Pannellum');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createViewer = useCallback(
    (container: string | HTMLElement, config: PannellumConfig): PannellumViewer | null => {
      if (!isLoaded || !window.pannellum) {
        return null;
      }

      try {
        return window.pannellum.viewer(container, config);
      } catch (err) {
        console.error('Error creating Pannellum viewer:', err);
        return null;
      }
    },
    [isLoaded]
  );

  return {
    isLoaded,
    isLoading,
    error,
    createViewer,
    load,
  };
}

export default usePannellum;
